import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useSession } from '../hooks/useSession'
import { SignInPanel } from '../components/auth/SignInPanel'

type PageState = 'probing' | 'sign-in' | 'magic-sent' | 'verifying' | 'ready' | 'approved' | 'denied' | 'expired' | 'invalid' | 'error'

export default function DeviceApprove() {
  const [params] = useSearchParams()
  const rawCode = params.get('user_code') ?? ''
  const userCode = rawCode.replace(/-/g, '').toUpperCase()

  const { status, session } = useSession()

  const [page, setPage]   = useState<PageState>('probing')
  const [busy, setBusy]   = useState(false)
  const [sentTo, setSentTo] = useState('')
  const [err, setErr]     = useState('')

  // Redirect URL so magic-link / GitHub OAuth lands back here with the code.
  const callbackURL = rawCode ? `/device?user_code=${rawCode}` : '/device'

  // Once we know session status, decide next state.
  useEffect(() => {
    if (status === 'probing') return
    if (status === 'signed-out') { setPage('sign-in'); return }
    // Signed in — verify the code if we have one.
    if (userCode) verifyCode()
    else setPage('ready') // no code in URL, show manual entry form
  }, [status]) // eslint-disable-line react-hooks/exhaustive-deps

  async function verifyCode(code = userCode) {
    setPage('verifying')
    setErr('')
    try {
      const res = await fetch(`/api/auth/device?user_code=${encodeURIComponent(code)}`, { credentials: 'include' })
      const body = await res.json().catch(() => ({})) as { status?: string; error?: string; error_description?: string }
      if (res.status === 400 && body.error === 'invalid_request') { setPage('invalid'); return }
      if (res.status === 400 && body.error === 'expired_token')   { setPage('expired'); return }
      if (!res.ok) { setErr(body.error_description ?? body.error ?? `HTTP ${res.status}`); setPage('error'); return }
      if (body.status === 'approved') { setPage('approved'); return }
      if (body.status === 'denied')   { setPage('denied');   return }
      setPage('ready')
    } catch {
      setErr('Network error — check your connection.')
      setPage('error')
    }
  }

  async function approve() {
    setBusy(true); setErr('')
    try {
      const res = await fetch('/api/auth/device/approve', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userCode }),
      })
      const body = await res.json().catch(() => ({})) as { success?: boolean; error?: string; error_description?: string }
      if (!res.ok) { setErr(body.error_description ?? body.error ?? `HTTP ${res.status}`); return }
      setPage('approved')
    } catch {
      setErr('Network error — check your connection.')
    } finally { setBusy(false) }
  }

  async function deny() {
    setBusy(true); setErr('')
    try {
      const res = await fetch('/api/auth/device/deny', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userCode }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error_description?: string; error?: string }
        setErr(body.error_description ?? body.error ?? `HTTP ${res.status}`)
        return
      }
      setPage('denied')
    } catch {
      setErr('Network error — check your connection.')
    } finally { setBusy(false) }
  }

  function handleMagicLinkSent(emailAddress: string) {
    setSentTo(emailAddress)
    setPage('magic-sent')
  }

  // ── Shared chrome ─────────────────────────────────────────────────────

  const wrap = (children: React.ReactNode) => (
    <div style={{ minHeight: 'calc(100vh - 53px)', padding: '80px 0' }}>
      <div className="container" style={{ maxWidth: 480 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          <span className="num">~</span>device login
        </div>
        {children}
      </div>
    </div>
  )

  // ── States ─────────────────────────────────────────────────────────────

  if (page === 'probing' || page === 'verifying') return wrap(
    <p className="muted mono" style={{ fontSize: 13 }}>
      {page === 'probing' ? 'Checking session…' : 'Verifying code…'}
    </p>
  )

  if (page === 'magic-sent') return wrap(
    <>
      <h1 className="h1">Check your email.</h1>
      <p className="lede" style={{ marginTop: 14 }}>
        We sent a sign-in link to{' '}
        <strong style={{ color: 'var(--fg)' }}>{sentTo}</strong>. The link expires in 5 minutes.
        After signing in you'll be brought back here to approve the CLI.
      </p>
      <p className="muted-2" style={{ fontSize: 12, marginTop: 24 }}>
        Didn't arrive? Check spam, or{' '}
        <span onClick={() => setPage('sign-in')} style={{ color: 'var(--accent)', cursor: 'pointer' }}>
          try again →
        </span>
      </p>
    </>
  )

  if (page === 'sign-in') return wrap(
    <>
      <h1 className="h1">Sign in to approve.</h1>
      <p className="lede" style={{ marginTop: 14, marginBottom: 28 }}>
        The CLI is requesting access to your account. Sign in to approve or deny it.
      </p>
      <SignInPanel callbackURL={callbackURL} onMagicLinkSent={handleMagicLinkSent} />
    </>
  )

  if (page === 'approved') return wrap(
    <>
      <h1 className="h1" style={{ color: 'var(--lime)' }}>Approved.</h1>
      <p className="lede" style={{ marginTop: 14 }}>
        The CLI is now signed in as{' '}
        <strong style={{ color: 'var(--fg)' }}>{session?.user.email ?? session?.user.name ?? 'you'}</strong>.
        You can close this tab.
      </p>
      <p className="muted-2" style={{ fontSize: 12, marginTop: 24 }}>
        The CLI will automatically receive a key and save it to{' '}
        <code className="mono">~/.config/instadash/config.json</code>.
      </p>
    </>
  )

  if (page === 'denied') return wrap(
    <>
      <h1 className="h1">Denied.</h1>
      <p className="lede" style={{ marginTop: 14 }}>
        The CLI request was denied. You can close this tab.
      </p>
    </>
  )

  if (page === 'expired') return wrap(
    <>
      <h1 className="h1">Code expired.</h1>
      <p className="lede" style={{ marginTop: 14 }}>
        This device code has expired. Run <code className="mono">instadash login</code> again to get a fresh one.
      </p>
    </>
  )

  if (page === 'invalid') return wrap(
    <>
      <h1 className="h1">Invalid code.</h1>
      <p className="lede" style={{ marginTop: 14 }}>
        That code wasn't found. Double-check you typed it correctly, or run{' '}
        <code className="mono">instadash login</code> again.
      </p>
    </>
  )

  if (page === 'error') return wrap(
    <>
      <h1 className="h1">Something went wrong.</h1>
      {err && <p style={{ color: 'var(--err)', fontSize: 14, marginTop: 14 }}>{err}</p>}
    </>
  )

  // ── Ready: approve / deny ────────────────────────────────────────────

  return wrap(
    <>
      <h1 className="h1">Approve CLI access?</h1>
      <p className="lede" style={{ marginTop: 14, marginBottom: 28 }}>
        The Instadash CLI on your device is requesting access to your account{' '}
        <strong style={{ color: 'var(--fg)' }}>{session?.user.email ?? session?.user.name}</strong>.
      </p>

      {userCode && (
        <div className="card card-pad" style={{ marginBottom: 28, borderColor: 'var(--accent-line)' }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>device code</div>
          <div className="mono" style={{ fontSize: 28, letterSpacing: '0.18em', color: 'var(--lime)', fontWeight: 700 }}>
            {userCode.slice(0, 4)}-{userCode.slice(4)}
          </div>
        </div>
      )}

      {err && (
        <div style={{ padding: '12px 16px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, color: 'var(--err)', fontSize: 13, marginBottom: 20 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={approve}
          disabled={busy}
          className="btn btn-primary btn-lg"
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {busy ? 'Approving…' : 'Approve →'}
        </button>
        <button
          onClick={deny}
          disabled={busy}
          className="btn btn-ghost btn-lg"
          style={{ justifyContent: 'center', minWidth: 100 }}
        >
          Deny
        </button>
      </div>

      <p className="muted-2" style={{ fontSize: 12, marginTop: 24, lineHeight: 1.6 }}>
        Approving will give the CLI access to your grids. You can revoke this at any time from{' '}
        <a href="/get-started" style={{ color: 'var(--accent)' }}>your API keys</a>.
      </p>
    </>
  )
}
